import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { api } from '../lib/api';
import { messageForError } from '../lib/errors';
import { forgotPasswordFormSchema, parseForm } from '../lib/forms';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = parseForm(forgotPasswordFormSchema, e.currentTarget);
    if (r.errors) return setFieldError(r.errors['email']);
    setFieldError(undefined);
    setError(null);
    setBusy(true);
    try {
      await api.auth.forgotPassword(r.data);
      setSent(true);
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We’ll email you a link if the address has an account."
    >
      {sent ? (
        <Alert tone="success">
          If that address has an account, a reset link is on its way. It expires in an hour.
        </Alert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          {error ? <Alert tone="error">{error}</Alert> : null}
          <Field
            id="email"
            name="email"
            label="Email"
            type="email"
            autoComplete="username"
            inputMode="email"
            required
            error={fieldError}
          />
          <Button type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
      <Link
        to="/login"
        className="text-center text-sm font-medium text-brand underline-offset-2 hover:underline"
      >
        Back to sign in
      </Link>
    </AuthLayout>
  );
}
